<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!submitting"
    :show-header="false"
    :style="{ width: '480px', maxWidth: '95vw' }"
    :content-style="{ padding: '0', borderRadius: '16px' }"
    :pt="{ root: { class: 'auto-recharge-form-root' } }"
    @update:visible="onVisibleChange"
  >
    <div class="auto-recharge-form">
      <header class="auto-recharge-form__header">
        <div class="auto-recharge-form__header-icon">
          <i class="fa-regular fa-arrows-rotate" />
        </div>
        <div class="auto-recharge-form__header-text">
          <h3 class="auto-recharge-form__title">
            {{ mode === 'enable'
              ? t('billing.autoRechargeForm.titleEnable')
              : t('billing.autoRechargeForm.titleEdit') }}
          </h3>
          <p class="auto-recharge-form__subtitle">
            {{ t('billing.autoRechargeForm.subtitle') }}
          </p>
        </div>
        <button
          type="button"
          class="auto-recharge-form__close"
          :disabled="submitting"
          :aria-label="t('billing.autoRechargeForm.cancel')"
          @click="onCancel"
        >
          <i class="fa-regular fa-xmark" />
        </button>
      </header>

      <div class="auto-recharge-form__body">
        <div class="auto-recharge-form__grid">
          <div class="auto-recharge-form__field">
            <label class="auto-recharge-form__field-label">
              {{ t('billing.autoRechargeForm.thresholdLabel') }}
            </label>
            <div class="auto-recharge-form__input">
              <InputNumber
                :model-value="threshold"
                :min="1"
                :max="10000"
                :show-buttons="false"
                :disabled="submitting"
                :input-class="['auto-recharge-form__field-input']"
                @update:model-value="onThresholdChange"
              />
              <span class="auto-recharge-form__input-currency">{{ currencySymbol }}</span>
            </div>
            <div class="auto-recharge-form__field-help">
              {{ t('billing.autoRechargeForm.thresholdHelp') }}
            </div>
          </div>

          <div class="auto-recharge-form__field">
            <label class="auto-recharge-form__field-label">
              {{ t('billing.autoRechargeForm.rechargeAmountLabel') }}
            </label>
            <div class="auto-recharge-form__input">
              <InputNumber
                :model-value="rechargeAmount"
                :min="1"
                :max="10000"
                :show-buttons="false"
                :disabled="submitting"
                :input-class="['auto-recharge-form__field-input']"
                @update:model-value="onAmountChange"
              />
              <span class="auto-recharge-form__input-currency">{{ currencySymbol }}</span>
            </div>
            <div class="auto-recharge-form__field-help">
              {{ t('billing.autoRechargeForm.rechargeAmountHelp') }}
            </div>
          </div>
        </div>

        <div
          v-if="isValid"
          class="auto-recharge-form__summary"
        >
          <i class="fa-regular fa-circle-info" />
          <span>
            {{ t('billing.autoRechargeForm.summary', {
              threshold: formatAmount(threshold ?? 0),
              amount: formatAmount(rechargeAmount ?? 0),
            }) }}
          </span>
        </div>

        <div
          v-if="errorMessage"
          class="auto-recharge-form__error"
        >
          <i class="fa-regular fa-circle-exclamation" />
          <span>{{ errorMessage }}</span>
        </div>
      </div>

      <footer class="auto-recharge-form__foot">
        <button
          type="button"
          class="auto-recharge-form__btn auto-recharge-form__btn--cancel"
          :disabled="submitting"
          @click="onCancel"
        >
          {{ t('billing.autoRechargeForm.cancel') }}
        </button>
        <button
          type="button"
          class="auto-recharge-form__btn auto-recharge-form__btn--primary"
          :disabled="submitting || !isValid"
          @click="onConfirm"
        >
          <i
            v-if="submitting"
            class="fa-regular fa-spinner-third fa-spin"
          />
          <span>
            {{ mode === 'enable'
              ? t('billing.autoRechargeForm.confirmEnable')
              : t('billing.autoRechargeForm.confirmEdit') }}
          </span>
        </button>
      </footer>
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import {
  billingCurrencySymbol,
  formatBillingAmount,
} from '@/composables/billing/formatAmount';

export type AutoRechargeFormMode = 'enable' | 'edit';

export interface AutoRechargeFormValue {
  threshold: number;
  rechargeAmount: number;
}

export default defineComponent({
  name: 'AutoRechargeFormDialog',

  components: { Dialog, InputNumber },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    /** `enable` = first activation, `edit` = modify existing config. */
    mode: {
      type: String as PropType<AutoRechargeFormMode>,
      default: 'enable',
    },

    /** Pre-filled values (current config or defaults for first activation). */
    initialThreshold: {
      type: Number as PropType<number | null>,
      default: null,
    },

    initialRechargeAmount: {
      type: Number as PropType<number | null>,
      default: null,
    },

    submitting: {
      type: Boolean,
      default: false,
    },

    errorMessage: {
      type: String as PropType<string | null>,
      default: null,
    },

    /** Shop billing currency — drives the input suffix + amount format. */
    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },
  },

  emits: ['update:visible', 'confirm', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const currencySymbol = computed(() => billingCurrencySymbol(props.currency));

    // Local working copy — committed via `confirm` event, dropped on cancel.
    const threshold = ref<number | null>(props.initialThreshold ?? 10);
    const rechargeAmount = ref<number | null>(props.initialRechargeAmount ?? 25);

    // Re-seed when the dialog re-opens with different initial values (e.g.
    // switching from enable-flow to edit-flow without unmounting).
    watch(
      () => props.visible,
      (v) => {
        if (!v) return;
        threshold.value = props.initialThreshold ?? 10;
        rechargeAmount.value = props.initialRechargeAmount ?? 25;
      },
    );

    const isValid = computed(() => (
      !!threshold.value && !!rechargeAmount.value
      && threshold.value > 0 && rechargeAmount.value > 0
    ));

    const onThresholdChange = (v: number | null | undefined) => {
      threshold.value = v == null ? null : Number(v);
    };
    const onAmountChange = (v: number | null | undefined) => {
      rechargeAmount.value = v == null ? null : Number(v);
    };

    const onConfirm = () => {
      if (!isValid.value || props.submitting) return;
      emit('confirm', {
        threshold: threshold.value!,
        rechargeAmount: rechargeAmount.value!,
      } as AutoRechargeFormValue);
    };

    const onCancel = () => {
      if (props.submitting) return;
      emit('cancel');
      emit('update:visible', false);
    };

    const onVisibleChange = (v: boolean) => {
      if (!v && !props.submitting) {
        emit('cancel');
        emit('update:visible', false);
      }
    };

    const formatAmount = (n: number) => formatBillingAmount(n, props.currency);

    return {
      t,
      threshold,
      rechargeAmount,
      isValid,
      currencySymbol,
      onThresholdChange,
      onAmountChange,
      onConfirm,
      onCancel,
      onVisibleChange,
      formatAmount,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.auto-recharge-form {
  &__header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 22px 24px 18px;
    border-bottom: 1px solid #f1f5f9;
  }

  &__header-icon {
    flex-shrink: 0;
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(148, 200, 64, 0.16) 0%, rgba(148, 200, 64, 0.04) 100%);
    color: $brand-color-primary;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.0625rem;
  }

  &__header-text { flex: 1; min-width: 0; }

  &__title {
    font-size: 1.0625rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.01em;
    line-height: 1.25;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin: 3px 0 0;
    line-height: 1.5;
  }

  &__close {
    border: none;
    background: transparent;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    cursor: pointer;
    transition: all 160ms ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      background: #f1f5f9;
      color: #0f172a;
    }

    &:disabled { opacity: 0.4; cursor: not-allowed; }
    i { font-size: 0.9375rem; }
  }

  &__body {
    padding: 22px 24px 18px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;

    @media (max-width: 520px) { grid-template-columns: 1fr; }
  }

  &__field { display: flex; flex-direction: column; }

  &__field-label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 8px;
  }

  &__input {
    position: relative;
    display: flex;
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid #e2e8f0;
    background: #fff;
    transition: border-color 160ms ease, box-shadow 160ms ease;

    &:focus-within {
      border-color: $brand-color-primary;
      box-shadow: 0 0 0 4px rgba(148, 200, 64, 0.16);
    }
  }

  :deep(.p-inputnumber) { display: flex; width: 100%; height: 48px; }

  :deep(.auto-recharge-form__field-input) {
    width: 100%;
    flex: 1;
    height: 48px;
    box-sizing: border-box;
    padding: 0 40px 0 14px !important;
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    border: none;
    background: transparent;
    text-align: center;
    font-variant-numeric: tabular-nums;
    margin: 0;

    &:focus { outline: none; box-shadow: none; }
  }

  &__input-currency {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    font-size: 0.9375rem;
    font-weight: 600;
    pointer-events: none;
  }

  &__field-help {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-top: 6px;
    line-height: 1.4;
  }

  &__summary {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #f6faff;
    border-radius: 10px;
    padding: 11px 13px;
    color: #475569;
    font-size: 0.8125rem;
    line-height: 1.5;

    i { color: #1d4ed8; margin-top: 2px; flex-shrink: 0; }
  }

  &__error {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: #fef2f2;
    border: 1px solid #fee2e2;
    border-radius: 10px;
    padding: 11px 13px;
    color: #991b1b;
    font-size: 0.8125rem;
    line-height: 1.5;

    i { color: #dc2626; margin-top: 2px; flex-shrink: 0; font-size: 0.9375rem; }
  }

  &__foot {
    padding: 16px 24px 20px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    border-top: 1px solid #f1f5f9;
  }

  &__btn {
    padding: 9px 18px;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 160ms ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    &:disabled { opacity: 0.55; cursor: not-allowed; }

    &--cancel {
      background: #fff;
      border-color: #e2e8f0;
      color: #475569;

      &:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #cbd5e1;
        color: #0f172a;
      }
    }

    &--primary {
      background: $brand-color-primary;
      color: #fff;
      box-shadow: 0 4px 12px -3px rgba(148, 200, 64, 0.45);

      &:hover:not(:disabled) {
        background: darken($brand-color-primary, 5%);
        transform: translateY(-1px);
      }
      &:active:not(:disabled) { transform: translateY(0); }
    }
  }
}
</style>

<style lang="scss">
.auto-recharge-form-root.p-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 80px -24px rgba(15, 23, 42, 0.32);

  .p-dialog-content {
    padding: 0 !important;
    border-radius: 0;
  }
}
</style>
